import React, { useEffect, useState } from 'react';
import { Images, X } from 'lucide-react';
import { logger } from '../utils/logger';

interface ImageLibraryPanelProps {
  onSelectImage: (imageUrl: string) => void;
  onClose: () => void;
}

interface LibraryImage {
  id: number;
  url: string;
  created_at: string;
}

export const ImageLibraryPanel: React.FC<ImageLibraryPanelProps> = ({
  onSelectImage,
  onClose,
}) => {
  const [images, setImages] = useState<LibraryImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllImages();
  }, []);

  const loadAllImages = async () => {
    try {
      const response = await fetch('/api/v1/slides/0/nodes/recent_images?limit=100', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load images');

      const data = await response.json();
      setImages(data.images);
      logger.debug('api', 'Loaded library images', { count: data.images.length });
    } catch (error) {
      logger.error('api', 'Failed to load library images', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-[rgba(32,40,53,0.95)] border-2 border-[#DFE4EA] backdrop-blur-[12.5px] rounded-lg w-[50vw] h-[50vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#DFE4EA]">
          <div className="flex items-center gap-3">
            <Images size={24} className="text-[#DFE4EA]" />
            <h2 className="font-medium text-[20px] text-[#DFE4EA]">Image Library</h2>
          </div>
          <button
            onClick={onClose}
            className="text-[#DFE4EA] hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-[#DFE4EA]">Loading images...</div>
            </div>
          ) : images.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-[#DFE4EA]">No images found</div>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-4">
              {images.map((image) => (
                <button
                  key={image.id}
                  onClick={() => {
                    onSelectImage(image.url);
                    onClose();
                  }}
                  className="aspect-square bg-[rgba(223,228,234,0.2)] border-2 border-[#DFE4EA] rounded hover:bg-[rgba(223,228,234,0.3)] transition-colors overflow-hidden"
                >
                  <img
                    src={image.url}
                    alt="Library image"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
